import Capabilities from '@utils/capabilities'
import {
  LOCAL_DRAFT_KEY_PREFIX,
  LOCAL_EDITS_KEY_PREFIX,
  PRODUCT_VIEW_TYPE_KEY,
  PROFILE_EPOCH_KEY,
  PURCHASING_QUANTITY_KEY,
  SCORES_VIEW_TYPE_KEY,
  THEME_TYPE_KEY,
  ThemeType,
  ViewType,
} from '@utils/global'

import type { SessionUser } from '@stores/globalData/server-side'

class Storage {
  // region Products View Type
  get productsViewType(): ViewType {
    let viewType = ViewType.THUMBNAIL
    if (Capabilities.hasStorage()) {
      const localStorageViewType = localStorage.getItem(PRODUCT_VIEW_TYPE_KEY)
      if (Object.values(ViewType).includes(localStorageViewType as any))
        viewType = localStorageViewType as ViewType
    }
    return viewType
  }

  set productsViewType(type: ViewType) {
    if (Capabilities.hasStorage() && [ViewType.THUMBNAIL, ViewType.LIST].includes(type)) {
      localStorage.setItem(PRODUCT_VIEW_TYPE_KEY, type)
    }
  }
  // endregion Products View Type

  // region Scores View Type
  get scoresViewType(): ViewType {
    let viewType = ViewType.LIST
    if (Capabilities.hasStorage()) {
      const localStorageViewType = localStorage.getItem(SCORES_VIEW_TYPE_KEY)
      if (Object.values(ViewType).includes(localStorageViewType as any))
        viewType = localStorageViewType as ViewType
    }
    return viewType
  }

  set scoresViewType(type: ViewType) {
    if (Capabilities.hasStorage() && [ViewType.LIST, ViewType.GRID].includes(type)) {
      localStorage.setItem(SCORES_VIEW_TYPE_KEY, type)
    }
  }
  // endregion Scores View Type

  // region Local Edits
  hasLocalEdits(externalId: string): boolean {
    if (Capabilities.hasStorage()) {
      const item = localStorage.getItem(`${LOCAL_EDITS_KEY_PREFIX}${externalId}`)
      if (item) {
        const itemData = JSON.parse(item)
        return itemData.draft !== null || itemData.edits.length > 0
      }
    }
    return false
  }
  // endregion Local Edits

  // region Local Draft
  hasLocalDraft(externalId: string, user: SessionUser | null): boolean {
    if (Capabilities.hasStorage() && user) {
      const item = localStorage.getItem(`${LOCAL_DRAFT_KEY_PREFIX}${externalId}`)
      if (item) {
        const itemData = JSON.parse(item)
        // We will honor an old local draft that has no recorded ownership, OR a new-style one if the ID matches the session.
        return !itemData.user_id || itemData.user_id === user.external_id
      }
    }
    return false
  }
  // endregion Local Draft

  // region Theme
  get theme(): ThemeType {
    let themeType = ThemeType.LIGHT
    if (Capabilities.hasStorage()) {
      const localStorageThemeType = localStorage.getItem(THEME_TYPE_KEY)
      if (Object.values(ThemeType).includes(localStorageThemeType as any))
        themeType = localStorageThemeType as ThemeType
    }
    return themeType
  }

  set theme(themeType: ThemeType) {
    if (Capabilities.hasStorage()) {
      localStorage.setItem(THEME_TYPE_KEY, themeType)
    }
  }
  // endregion Theme

  // region Purchasing
  getPurchasingQuantity(uuid: string): number | null {
    if (Capabilities.hasStorage()) {
      const localStorageQuantity = localStorage.getItem(`${PURCHASING_QUANTITY_KEY}_${uuid}`)
      if (localStorageQuantity && Number.isInteger(Number(localStorageQuantity)))
        return Number(localStorageQuantity)
    }
    return null
  }

  setPurchasingQuantity(uuid: string, quantity: number | null): void {
    if (Capabilities.hasStorage()) {
      if (uuid) localStorage.setItem(`${PURCHASING_QUANTITY_KEY}_${uuid}`, `${quantity}`)
      else localStorage.removeItem(`${PURCHASING_QUANTITY_KEY}_${uuid}`)
    }
  }
  // endregion Purchasing

  // region Profile epoch
  get profileEpoch(): string | null {
    if (Capabilities.hasSessionStorage()) {
      return sessionStorage.getItem(PROFILE_EPOCH_KEY)
    }
    return null
  }

  set profileEpoch(epoch: string | null) {
    if (Capabilities.hasSessionStorage()) {
      if (epoch) sessionStorage.setItem(PROFILE_EPOCH_KEY, epoch)
      else sessionStorage.removeItem(PROFILE_EPOCH_KEY)
    }
  }
  // endregion Profile epoch
}

const storage = new Storage()
export default storage
